import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, take, tap } from 'rxjs/operators';

import { TrackingService } from '@pe/checkout/api';
import { FlowState, PatchFlow, SetSplits, SplitState } from '@pe/checkout/store';
import { FlowInterface, FlowSplit, PaymentMethodEnum, ViewPaymentOption } from '@pe/checkout/types';

function generateReference(splits: FlowSplit[], reference: string, startNumber = 1) {
  const paymentNumber = !splits
    ? startNumber
    : splits.reduce((acc, item) => (item.isPaid) ? ++acc : acc, startNumber);

  const flowReference = reference?.split('-payment')?.[0];

  return `${flowReference}-payment${paymentNumber}`;
}

@Injectable({
  providedIn: 'root',
})
export class SplitsService {
  private readonly store = inject(Store);
  private readonly trackingService = inject(TrackingService);

  public readonly toggleSplitView$ = new BehaviorSubject<boolean>(false);
  public readonly preloadIssuer$ = new Subject<void>();
  public readonly continue$ = new Subject<void>();
  public readonly updateSplit$ = new Subject<FlowSplit>();

  public readonly paidSomeSplit$ = this.store.selectOnce(FlowState.splits).pipe(
    map(splits => !!splits?.length),
  );

  public useSplits = false;

  public readonly useSplit$: Observable<boolean> = merge(
    this.toggleSplitView$.pipe(
      filter(d => typeof d === 'boolean'),
      distinctUntilChanged(),
      map(Boolean)
    ),
    this.paidSomeSplit$.pipe(
      take(1)
    ),
  ).pipe(
    map(useSplit => useSplit && !!this.flow.apiCall?.allowSplitPayments),
    tap((useSplit) => {
      this.useSplits = useSplit;
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true,
    })
  );

  get splits(): FlowSplit[] {
    return this.store.selectSnapshot(SplitState.splits) ?? [];
  }

  get flow(): FlowInterface {
    return this.store.selectSnapshot(FlowState.flow);
  }

  get sum(): number {
    return this.splits.reduce((acc, split) => acc + split.amount.value || 0, 0);
  }

  hasSplit$(paymentMethod: PaymentMethodEnum): Observable<boolean> {
    return this.store.select(SplitState.splits).pipe(
      map(splits => splits.some(split => this.isUnpaidSplit(split, paymentMethod)))
    );
  }

  getSplit(paymentMethod: PaymentMethodEnum): FlowSplit {
    return this.splits.find(split => this.isUnpaidSplit(split, paymentMethod));
  }

  isUnpaidSplit(split: FlowSplit, paymentMethod: PaymentMethodEnum) {
    return !split?.isPaid && split.paymentMethod === paymentMethod;
  }

  public submitSplitFlow(): Observable<void> {
    const flowSplits = this.store.selectSnapshot(FlowState.splits);
    const flowId = this.store.selectSnapshot(FlowState.flowId);
    const splits = this.store.selectSnapshot(SplitState.splits);
    const payedSplits = flowSplits.filter(split => split.isPaid);
    const filteredSplits = splits.filter(split => split?.isPaid || split.amount.value).map((split, index) => ({
      ...split,
      reference: generateReference(flowSplits, this.flow.reference, index + 1),
    }));
    const activeSplit = filteredSplits
      .find(split => !split?.isPaid && split.amount.value);

    this.trackingService.doEmitCustomEvent(flowId, activeSplit.paymentMethod, 'customer_split_applied');

    return this.store.dispatch(new PatchFlow({
      referenceExtra: generateReference(flowSplits, this.flow.reference),
      splits: [...payedSplits, ...filteredSplits],
      connectionId: activeSplit.connectionId,
    })
    );
  }

  public splitAmount(connectionId: string): number {
    const split = this.splits.find(
      split => !split?.isPaid && split.amount.value && split.connectionId === connectionId
    );

    return split?.amount?.value;
  }

  public addDuplicate(amount: number, paymentOption: ViewPaymentOption): void {
    const split = this.mapSplitData(amount, paymentOption);
    const lastIndex = this.splits.map(item => item.paymentMethod).lastIndexOf(split.paymentMethod);
    const splits = [...this.splits];
    splits.splice(lastIndex + 1, 0, split);

    this.store.dispatch(new SetSplits(splits));
  }

  public addSplitOnce(amount: number, paymentOption: ViewPaymentOption): void {
    if (this.splits.some(split => !split.isPaid && split.paymentMethod === paymentOption.paymentMethod)) {
      return;
    }

    const flowSplits = this.store.selectSnapshot(FlowState.splits);
    const paidSplits = flowSplits.filter(item => item.isPaid);

    this.store.dispatch([
      new SetSplits([]),
      ...paidSplits?.length !== flowSplits.length ? [new PatchFlow({ splits: paidSplits })] : [],
    ]);
    this.addSplit(amount, paymentOption);
  }

  public addSplit(amount: number, paymentOption: ViewPaymentOption): void {
    const flowSplits = this.store.selectSnapshot(FlowState.splits);
    const newSplit = this.mapSplitData(amount, paymentOption);
    const splits = flowSplits.filter(item => this.isUnpaidSplit(item, newSplit.paymentMethod));

    const insertData = splits?.length ? splits : [newSplit];
    this.store.dispatch(new SetSplits([...this.splits, ...insertData]));
  }

  public updateSplit(
    amount: number,
    paymentOption: ViewPaymentOption,
    index?: number,
  ): void {
    const split = this.mapSplitData(amount, paymentOption);
    index >= 0
      ? this.updateFlowSplitByIndex(split, index)
      : this.updateFlowSplit(split);

    this.updateSplit$.next(split);
  }

  private updateFlowSplit(splitData: FlowSplit): void {
    const findIndex = this.splits.findIndex(
      split => !split?.isPaid && split.connectionId === splitData.connectionId
    );

    this.updateFlowSplitByIndex(splitData, findIndex);
  }

  private updateFlowSplitByIndex(splitData: FlowSplit, issuerIndex: number): void {
    const splits = [...this.splits];
    splits.splice(issuerIndex, 1, splitData);

    this.store.dispatch(new SetSplits(splits));
  }

  private mapSplitData(amount: number, paymentOption: ViewPaymentOption): FlowSplit {
    const { id, paymentMethod, label, paymentIssuer } = paymentOption;
    const flowSplits = this.store.selectSnapshot(FlowState.splits);

    return {
      type: 'payment',
      amount: {
        value: amount,
        currency: this.flow.currency,
      },
      paymentMethod: paymentMethod,
      paymentIssuer: paymentIssuer,
      reference: generateReference(flowSplits, this.flow.reference),
      connectionId: id,
      description: label,
    };
  }
}
